import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useLms } from '../../context/LmsContext';
import { Card, PageHeader, StatusBadge } from '../../components/ui';
import { byId, getCourse, getLatestExerciseSubmission, getLearningNavigation } from '../../utils/lmsSelectors';

function NavigationButtons({ assignment, nav }) {
  return (
    <div className="learner-nav-row">
      {nav.previous ? (
        <Link className="button button-ghost" to={nav.previous.path}>Previous: {nav.previous.title}</Link>
      ) : (
        <Link className="button button-ghost" to={`/learner/course/${assignment.id}`}>Back to course outline</Link>
      )}
      {nav.next ? (
        <Link className="button button-secondary" to={nav.next.path}>Next: {nav.next.title}</Link>
      ) : (
        <Link className="button button-secondary" to={`/learner/course/${assignment.id}`}>Course summary</Link>
      )}
    </div>
  );
}

export default function ExercisePage() {
  const { exerciseSubmissionId } = useParams();
  const [assignmentId, exerciseId] = exerciseSubmissionId.split(':');
  const { data, currentUser, submitExercise } = useLms();
  const navigate = useNavigate();
  const [submissionUrl, setSubmissionUrl] = useState('');
  const [submissionText, setSubmissionText] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const assignment = data.assignments.find((item) => item.id === assignmentId && item.assignedToUserId === currentUser.id);
  const exercise = byId(data.exercises || [], exerciseId);
  if (!assignment || !exercise) return <Card>Exercise not found or access removed.</Card>;

  const course = getCourse(data, assignment.courseId);
  const latest = getLatestExerciseSubmission(assignment, exercise.id);
  const nav = getLearningNavigation(data, assignment, 'exercise', exercise.id);
  const isWaitingReview = latest?.status === 'needs_review';
  const isPassed = latest?.status === 'passed';
  const canSubmit = submissionUrl.trim().includes('drive.google.com') && !isWaitingReview && !saving;

  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    setMessage('');
    setSaving(true);
    const response = await submitExercise(assignment.id, exercise.id, { submissionUrl: submissionUrl.trim(), submissionText }, currentUser.id);
    setSaving(false);
    if (!response.ok) {
      setMessage(response.message);
      return;
    }
    setMessage('Exercise submitted. Your trainer will check and grade it.');
    navigate(`/learner/course/${assignment.id}`);
  };

  return (
    <>
      <PageHeader
        title={exercise.title}
        eyebrow={course?.title}
        description={`Graded exercise • Passing score ${exercise.passingScore}% • Max score ${exercise.maxScore}`}
        action={<Link className="button button-secondary" to={`/learner/course/${assignment.id}`}>Back to course</Link>}
      />
      <NavigationButtons assignment={assignment} nav={nav} />
      <div className="grid two uneven">
        <Card>
          <div className="builder-topline">
            <StatusBadge status={latest?.status || 'not_started'} />
            {latest?.scorePercent !== undefined && latest?.scorePercent !== null && <span>Score: {latest.scorePercent}%</span>}
          </div>
          <h2>Exercise instructions</h2>
          <div className="lesson-content"><p>{exercise.instructions || 'Complete the exercise file and submit your completed Google Drive link.'}</p></div>
          {exercise.exerciseFileUrl && (
            <div className="resource-viewer-card exercise-resource-card">
              <div className="resource-viewer-header">
                <div>
                  <h3>{exercise.exerciseFileTitle || 'Exercise file'}</h3>
                  <p className="muted small">Open or download the trainer-provided Google Drive file/folder.</p>
                </div>
                <a className="button button-primary" href={exercise.exerciseFileUrl} target="_blank" rel="noreferrer">Open/download exercise file</a>
              </div>
            </div>
          )}
          {latest?.feedback && <p className="feedback-box">Trainer feedback: {latest.feedback}</p>}
        </Card>
        <Card>
          <h2>{isPassed ? 'Exercise completed' : isWaitingReview ? 'Waiting for trainer review' : 'Submit completed exercise'}</h2>
          {isWaitingReview && <p className="muted">Your submission is locked while the trainer is checking and grading it.</p>}
          {isPassed && <p className="muted">This exercise is passed. You can continue to the next required item.</p>}
          {!isWaitingReview && !isPassed && (
            <form className="form-stack" onSubmit={submit}>
              <label className="form-row">
                <span>Completed exercise Google Drive link</span>
                <input required type="url" placeholder="https://drive.google.com/..." value={submissionUrl} onChange={(event) => setSubmissionUrl(event.target.value)} />
              </label>
              <label className="form-row">
                <span>Notes for trainer</span>
                <textarea rows="6" placeholder="Add assumptions, notes, or questions for review..." value={submissionText} onChange={(event) => setSubmissionText(event.target.value)} />
              </label>
              <button className="button button-primary" type="submit" disabled={!canSubmit}>{saving ? 'Submitting...' : 'Submit exercise for grading'}</button>
              {!canSubmit && <p className="small muted">Paste a Google Drive link to your completed exercise before submitting.</p>}
            </form>
          )}
          {message && <p className="feedback-box">{message}</p>}
          {latest?.submissionUrl && <p className="feedback-box">Latest submission: <a href={latest.submissionUrl} target="_blank" rel="noreferrer">Open Drive link</a></p>}
        </Card>
      </div>
    </>
  );
}
